import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { DestinationChoicesModal } from "@/containers/RecordVideo/components/DestinationChoicesModal";
import { get } from "lodash";
import { Media, PricePlanTier } from "@/interfaces";
import { AppContext } from "@/providers/AppContextProvider";
import { URLS, useSocialCoachSession } from "@/utils";
import { useHistory, useLocation } from "react-router-dom";
import { VideoPageModal } from "@/containers/VideoPages/VideoPageModal";

import "./styles.scss";
import { ScreenCapture } from ".";
import { fromMedia } from "@/components/PostDialogs/SharedPostFlowDefaults";
import { PlayerPostDialog } from "@/components/PostDialogs/PlayerPostDialog";

const ScreenCapturePageFC: React.FC = () => {
  const history = useHistory();
  const location = useLocation<{ purpose?: string }>();
  const purpose: string = get(location, "state.purpose", "");
  const { isCoach } = useSocialCoachSession();
  const urls = isCoach ? URLS.coach : URLS.player;

  // Modal State
  const [showChoicesModal, setShowChoicesModal] = useState(false);
  const [showPostModal, setShowPostModal] = useState(false);
  const [showVideoPageModal, setShowVideoPageModal] = useState(false);
  const [selectedDestination, setSelectedDestination] = useState<"videoProject" | "post" | "videoPage">();

  // Recording State
  const [displaySeconds, setDisplaySeconds] = useState(0);

  // Media State
  const [recordedMedia, setRecordedMedia] = useState<Media>();

  const { userContext } = useContext(AppContext);
  const subscriptionType: string = get(userContext, "subscriptions[0].pricePlan.tier", "");
  const isSocialCoachPlus = subscriptionType === PricePlanTier.PLAYER_PLUS;

  useEffect(() => {
    if (recordedMedia && selectedDestination) {
      switch (selectedDestination) {
        case "videoProject":
          history.push(urls.newVideoProject, {
            preloadMedia: recordedMedia,
          });
          break;
        case "post":
          setShowPostModal(true);
          break;
        case "videoPage":
          setShowVideoPageModal(true);
          break;
      }
    }
  }, [recordedMedia, selectedDestination]);

  const backAction = (): void => {
    history.goBack();
  };

  const handleDone = useCallback(
    (media: Media, secondsRecorded: number) => {
      setRecordedMedia(media);
      setDisplaySeconds(secondsRecorded);
      if (purpose == "post" || purpose == "videoProject" || purpose == "videoPage") {
        setSelectedDestination(purpose);
      } else {
        setShowChoicesModal(true);
      }
    },
    [purpose]
  );

  const videoPageModalInitialObject = useMemo(() => {
    if (recordedMedia) return { media: recordedMedia };
    return undefined;
  }, [recordedMedia]);

  return (
    <div>
      <ScreenCapture onCancel={backAction} onDone={handleDone} />
      <DestinationChoicesModal
        open={showChoicesModal}
        hasSCPlus={isSocialCoachPlus}
        okHandler={choice => {
          setShowChoicesModal(false);
          setSelectedDestination(choice);
        }}
        onClose={() => setShowChoicesModal(false)}
      />
      {showPostModal && (
        <PlayerPostDialog
          open={showPostModal}
          handleClose={() => setShowPostModal(false)}
          defaults={fromMedia(recordedMedia, displaySeconds)}
        />
      )}
      <VideoPageModal
        open={showVideoPageModal}
        initialObject={videoPageModalInitialObject}
        onClose={() => setShowVideoPageModal(false)}
      />
    </div>
  );
};

export const ScreenCapturePage = ScreenCapturePageFC;
